import { apolloClient } from "@/api/apiClient";

/**
 * The cached fields whose value depends on who is asking, by the typename the
 * cache keys them under (`ROOT_QUERY` for the root fields). Evicted by
 * `storeUser` when the user id changes (SHO-827): a login into another
 * account, a logout (which registers a new device user). Linking a provider to
 * the current anonymous user keeps the id and touches nothing.
 *
 * Why evict rather than `refetchQueries`: a refetch only reaches the *active*
 * queries, and the logout case is an inactive one — log out from the profile,
 * then open a show visited earlier: its cached `GetShowLikeState` is served by
 * `cache-first` as is. An incomplete cache result makes Apollo refetch an
 * active query on its own and miss the cache for an inactive one on its next
 * mount, so one mechanism covers both. Not `resetStore` either: it refetches
 * `GetShow`, which hands the player a new `show` and remounts every slide
 * (`.claude/rules/episode-likes.md`).
 *
 * `Episode.likes` is not listed: evicting `liked` already makes the like state
 * query incomplete, and its refetch brings the count along. The home layout and
 * the section contents are: the layout drops a `hideIfEmpty` section (Continue
 * watching) per user, and a section's shows carry that user's progression. A
 * mounted home keeps its content meanwhile through `previousData`.
 *
 * A request already in flight with the previous token is not caught: its answer
 * lands after the eviction, and the refetch the eviction triggers is
 * deduplicated onto it (Apollo keys the dedup on query + variables, not on
 * headers). The window is the login callback itself; a reload clears it.
 *
 * `tests/unit/api/userScopedCache.test.ts` derives every `[AUTHENTICATED]`
 * field the project's queries select from the introspection and requires each
 * to be here or in `SHARED_AUTHENTICATED_FIELDS`, so a new one fails the suite
 * until someone decides.
 */
export const USER_SCOPED_FIELDS: Record<string, ReadonlyArray<string>> = {
  ROOT_QUERY: [
    "catalogLayout",
    "sectionContent",
    "bookmarkedShows",
    "recentlyViewed",
    // The account itself, which `UserInfosWatcher` copies into the auth store.
    "me",
    // Both tokenized for the current user.
    "oneLinkDeeplinkUrl",
    "supportToken",
    "yearlyWrapped",
  ],
  Show: ["isBookmarked", "progression"],
  Episode: ["liked"],
};

/**
 * Authenticated fields whose value does not depend on the user: the directive
 * only asks for a token. Left in the cache on an account change.
 */
export const SHARED_AUTHENTICATED_FIELDS: Record<
  string,
  ReadonlyArray<string>
> = {
  ROOT_QUERY: [
    // Keyed on the actor or the show; what is per user in their shows is on
    // the `Show` entities, evicted above.
    "actorShows",
    "relatedShows",
    // Keyed on the OAuth code of the login in progress.
    "getGoogleOAuthParameters",
  ],
};

export function evictUserScopedData() {
  // One transaction, so the watchers are notified once and each affected
  // query refetches once, not once per evicted field.
  apolloClient.cache.batch({
    update: (cache) => {
      // `Show:905`, `Episode:ep-1`, `ROOT_QUERY`.
      for (const id of Object.keys(cache.extract() as object)) {
        const typename = id.split(":")[0];
        if (!(typename in USER_SCOPED_FIELDS)) continue;
        for (const fieldName of USER_SCOPED_FIELDS[typename])
          cache.evict({ id, fieldName });
      }
    },
  });
  // The previous account's entities that only the evicted fields reached (its
  // lists, its `User`) are unreachable now; drop them.
  apolloClient.cache.gc();
}
